import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { fetcher } from "@/lib/api";
import { navConfig } from "@/config/navConfig";
import type { NavItem } from "@/config/navConfig";
import { useUserStore } from "@/store/useUserStore";
import type { UserRole } from "@/types/userType";

/** One result row returned by the backend, regardless of group. */
export interface SearchItem {
  id: number | string;
  title: string;
  subtitle: string | null;
  url: string;
}

/** A backend group (Courses, Learners, …). */
export interface SearchGroup {
  key: string;
  label: string;
  items: SearchItem[];
}

interface SearchResponse {
  success: boolean;
  query: string;
  groups: SearchGroup[];
}

/**
 * Walks navConfig and flattens every entry the current user can see into a
 * single list. This mirrors {@link Sidebar}'s filterNav so the dropdown's
 * "Pages" section can never offer a destination the sidebar wouldn't.
 */
const buildPermittedPages = (
  userRoles: UserRole[],
  userPermissions: string[],
  isSuperAdmin: boolean,
): { title: string; href: string; parent?: string }[] => {
  const hasRoleAccess = (roles?: UserRole[]) => {
    if (!roles || roles.length === 0) return true;
    return roles.some((r) => userRoles.includes(r));
  };
  const hasPermissionAccess = (required?: string[]) => {
    if (!required || required.length === 0) return true;
    if (isSuperAdmin) return true;
    return required.every((p) => userPermissions.includes(p));
  };

  const out: { title: string; href: string; parent?: string }[] = [];
  for (const item of navConfig as NavItem[]) {
    if (item.hidden) {
      continue;
    }
    if (!hasRoleAccess(item.roles) || !hasPermissionAccess(item.requiredPermissions)) {
      continue;
    }
    out.push({ title: item.title, href: item.href });
    if (item.items?.length) {
      for (const sub of item.items) {
        if (!hasRoleAccess(sub.roles as UserRole[]) || !hasPermissionAccess(sub.requiredPermissions)) {
          continue;
        }
        out.push({ title: sub.title, href: sub.href, parent: item.title });
      }
    }
  }
  return out;
};

/**
 * Debounced global search powering the Topbar dropdown.
 *
 * Returns a "Pages" group built locally (so it works offline and stays in
 * lockstep with the sidebar) plus all entity groups returned by /api/search.
 * Backend gates the entity groups by permission; we gate "Pages" the same
 * way so the two sections share a single source of truth (navConfig).
 */
export const useGlobalSearch = (rawQuery: string, debounceMs = 250) => {
  const user = useUserStore((s) => s.user);
  const role = useUserStore((s) => s.role);
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin);

  const [debouncedQuery, setDebouncedQuery] = useState(rawQuery);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(rawQuery.trim()), debounceMs);
    return () => clearTimeout(id);
  }, [rawQuery, debounceMs]);

  const enabled = debouncedQuery.length >= 2;

  const remote = useQuery<SearchResponse>({
    queryKey: ["global-search", debouncedQuery],
    queryFn: () => fetcher<SearchResponse>(`/search?q=${encodeURIComponent(debouncedQuery)}`),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    // No retries: a search 500 is almost always a real server bug. Retrying
    // 3× with exponential backoff just hides the error from the user for
    // 30+ seconds before we finally surface it in the dropdown.
    retry: false,
  });

  const pagesGroup = useMemo<SearchGroup | null>(() => {
    if (!enabled) return null;
    const userRoles: UserRole[] = role ? [role as UserRole] : ((user?.roles as UserRole[]) || []);
    const perms = (user as any)?.permissions || [];
    const permitted = buildPermittedPages(userRoles, perms, isSuperAdmin);

    const needle = debouncedQuery.toLowerCase();
    const matches = permitted
      .filter((p) => p.title.toLowerCase().includes(needle))
      .slice(0, 6)
      .map((p, idx) => ({
        id: `page-${idx}-${p.href}`,
        title: p.title,
        subtitle: p.parent ?? null,
        url: p.href,
      }));

    if (matches.length === 0) return null;
    return { key: "pages", label: "Pages", items: matches };
  }, [enabled, debouncedQuery, role, user, isSuperAdmin]);

  const groups = useMemo<SearchGroup[]>(() => {
    const remoteGroups = remote.data?.groups ?? [];
    return pagesGroup ? [pagesGroup, ...remoteGroups] : remoteGroups;
  }, [pagesGroup, remote.data]);

  return {
    query: debouncedQuery,
    enabled,
    isLoading: enabled && remote.isFetching,
    isError: remote.isError,
    errorMessage: remote.error instanceof Error ? remote.error.message : null,
    groups,
    isEmpty: enabled && !remote.isFetching && !remote.isError && groups.every((g) => g.items.length === 0),
  };
};
